<ng-container *transloco="let t">
    <!-- Hidden file input for APPEND -->
    <input
        #appendFileInput
        type="file"
        accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        style="display: none"
        (change)="onAppendFileSelected($event)"
    />
    <input
        #coreLogicFileInput
        type="file"
        accept=".csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
        style="display: none"
        (change)="onCoreLogicFileSelected($event)"
    />

    <div
        class="dialog-container sai-dialog-shell add-tables-dialog"
        [dir]="lang === 'en' ? 'ltr' : 'rtl'"
    >
        <app-sai-dialog-header
            iconLigature="table_chart"
            [showClose]="!isUploadLocked"
            (closed)="closeAddTablesDialog()"
        >
            <ng-container saiDialogHeaderTitle>
                {{ t(dialogTitleKey) }}
                {{ t("Table") }}
            </ng-container>
            <div
                class="header-subtitle"
                *ngIf="form.get('name')?.value || data1?.tableUserName || data1?.tableName"
                saiDialogHeaderSubtitle
            >
                <strong>{{ form.get("name")?.value || data1?.tableUserName || data1?.tableName }}</strong>
            </div>
        </app-sai-dialog-header>

        <div class="add-tables-body-wrap">
            <div
                *ngIf="isUploadOverlayVisible"
                class="add-tables-upload-overlay"
                role="status"
                aria-live="polite"
            >
                <div class="add-tables-upload-progress-card">
                    <div
                        class="add-tables-upload-progress-icon"
                        aria-hidden="true"
                    >
                        <mat-icon>cloud_upload</mat-icon>
                    </div>
                    <h3 class="add-tables-upload-progress-title">
                        {{ t("Uploading Table") }}
                    </h3>
                    <p
                        *ngIf="uploadSheetProgress as sheetProgress"
                        class="add-tables-upload-progress-status"
                    >
                        {{ t('Sheet {{current}} of {{total}}: {{name}}', sheetProgress) }}
                    </p>
                    <p class="add-tables-upload-progress-status">
                        {{ t(uploadProgressStatusKey) }}
                    </p>
                    <div class="add-tables-upload-progress-bar-wrap">
                        <mat-progress-bar
                            class="add-tables-upload-progress-bar"
                            [mode]="
                                showProgress ? 'determinate' : 'indeterminate'
                            "
                            [value]="progressValue"
                        ></mat-progress-bar>
                    </div>
                    <div
                        *ngIf="showProgress"
                        class="add-tables-upload-progress-percent"
                    >
                        {{ progressValue }}%
                    </div>
                </div>
            </div>

            <div *ngIf="isReadingUploadFiles" class="add-tables-reading-files" role="status">
                <app-loading-spinner
                    [diameter]="48"
                    [loadingText]="t('Reading files')"
                    [showLoadingText]="true"
                ></app-loading-spinner>
            </div>

            <app-workbook-sheet-picker
                *ngIf="isSheetPickerVisible"
                [groups]="pickerGroups"
                (confirmed)="onWorkbookSheetsChosen($event)"
                (cancelled)="closeAddTablesDialog()"
            ></app-workbook-sheet-picker>

            <div
                *ngIf="!isSheetPickerVisible && !isReadingUploadFiles"
                class="split-body add-tables-split-body"
                [class.add-tables-split-body--single-column]="
                    isAddModeSingleColumnLayout
                "
                [class.add-tables-split-body--preview-collapsed]="
                    isTablePreviewCollapsed && canToggleTablePreviewCollapse
                "
                [class.add-tables-split-body--uploading]="
                    isUploadOverlayVisible
                "
            >
            <div class="content content-left add-tables-content-left">

                <!-- Multi-sheet workbook: one table per chosen sheet, each edited on its own -->
                <div
                    *ngIf="sheetDrafts as drafts"
                    class="add-tables-sheet-tabs"
                    role="tablist"
                    [attr.aria-label]="t('Sheets to upload')"
                >
                    <button
                        *ngFor="let draft of drafts.drafts; let si = index; trackBy: trackBySheetDraft"
                        type="button"
                        role="tab"
                        class="add-tables-sheet-tab"
                        [class.add-tables-sheet-tab--active]="si === drafts.activeIndex"
                        [attr.aria-selected]="si === drafts.activeIndex"
                        [attr.title]="sheetTabTitle(draft)"
                        (click)="selectSheetDraft(si)"
                    >
                        <mat-icon class="icon-size-4" svgIcon="heroicons_outline:table-cells"></mat-icon>
                        <span class="add-tables-sheet-tab__name">{{ draft.label }}</span>
                        <span
                            *ngIf="sheetProblemCount(draft.key) as issues"
                            class="sai-badge sai-badge--danger add-tables-sheet-tab__issues"
                            [attr.aria-label]="t('{{count}} problems to fix', { count: issues })"
                        >{{ issues }}</span>
                    </button>
                </div>

                <!-- New table: reserved column names were renamed rather than blocking Save -->
                <div
                    *ngIf="renamedHeaders.length"
                    class="sai-note w-full mb-4"
                    role="status"
                >
                    <mat-icon class="sai-note__icon icon-size-5" svgIcon="heroicons_outline:information-circle"></mat-icon>
                    <span class="sai-note__text">
                        {{ t('Some column names are reserved by SAI, so they were renamed: {{list}}. You can rename them again in the table preview.', { list: renamedHeadersSummary }) }}
                    </span>
                </div>

                <!-- Append mode hint -->
                <div
                    *ngIf="isAppending"
                    class="w-full mb-4 text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-700/50 rounded-lg p-3"
                >
                    {{
                        t(
                            'Append edit mode: Adjust column names/types to match the original schema, then click "Append Now".'
                        )
                    }}
                </div>

                <form
                    [formGroup]="form"
                    class="flex flex-auto flex-wrap flex-col gap-4 w-full mb-0 add-tables-form"
                >
                <section class="add-tables-section-card add-tables-section-card--details">
                    <div class="add-tables-section-head add-tables-section-head--row">
                        <div class="add-tables-section-head-main">
                            <mat-icon class="add-tables-section-icon">edit_note</mat-icon>
                            <h2 class="add-tables-section-title">{{ t("Details") }}</h2>
                        </div>
                        <span class="add-tables-type-tag" *ngIf="tableTypeDisplay">
                            {{ tableTypeDisplay + ' ' +  'table'}}
                        </span>
                    </div>
                    <div class="add-tables-section-body">

            <!-- Name -->
            <div class="w-full">
                <mat-form-field class="w-full relative fuse-mat-dense" appearance="fill">
                    <mat-label>{{ t("Table Name") }}</mat-label>
                    <mat-icon
                        class="absolute top-[-1.6rem] p-2 text-2xl"
                        [ngClass]="lang === 'en' ? 'right-0' : 'left-0'"
                        [matTooltip]="t('Info about table name')"
                        >error_outline</mat-icon
                    >
                    <input
                        matInput
                        [readonly]="isTableNameReadOnly"
                        [attr.readonly]="isTableNameReadOnly ? '' : null"
                        type="text"
                        formControlName="name"
                        appNoSpecialChars
                    />
                </mat-form-field>
                <small
                    *ngIf="
                        form.get('name')?.touched &&
                        form.get('name')?.hasError('required')
                    "
                    class="text-red-600"
                >
                    {{ t("Table name is required") }}.
                </small>
                <small
                    *ngIf="
                        form.get('name')?.touched &&
                        form.get('name')?.hasError('invalidName')
                    "
                    class="text-red-600 block"
                >
                    {{
                        t(
                            "Allowed: letters/numbers/underscore, must start with letter or underscore"
                        )
                    }}.
                </small>
                <small
                    *ngIf="
                        form.get('name')?.touched &&
                        form.get('name')?.hasError('maxLengthExceeded')
                    "
                    class="text-red-600 block"
                >
                    {{ t("Max length is 63 characters") }}.
                </small>
                <small *ngIf="form.get('name')?.hasError('nameTaken')" class="text-red-600 block">
                    {{ t("Table name is already used by your account.") }}
                </small>
                <small *ngIf="form.get('name')?.hasError('duplicateName')" class="text-red-600 block">
                    {{ t("Two selected sheets use the same table name. Give each sheet its own table name.") }}
                </small>
            </div>

            <!-- Description / Structure for View and external engine tables -->
            <mat-form-field
                *ngIf="data1?.type === 'View' || isExternalTableDialog()"
                class="w-full flex flex-col relative mt-3 add-tables-field-scroll-through fuse-mat-dense"
                [subscriptSizing]="'dynamic'"
            >
                <mat-label>{{ t("Table Description") }}</mat-label>
                <textarea
                    matInput
                    formControlName="description"
                    maxlength="10000"
                    [rows]="4"
                    [readonly]="
                        isForeignReadOnlyTable ||
                        isExternalManagerStaffReadOnly
                    "
                    [spellcheck]="true"
                    cdkTextareaAutosize
                    cdkAutosizeMaxRows="4"
                    class="pe-10"
                ></textarea>
                <mat-hint align="end"
                    >{{
                        form.get("description")?.value?.length || 0
                    }}/10000</mat-hint
                >
                <small
                    *ngIf="
                        form.get('description')?.touched &&
                        form.get('description')?.hasError('maxlength')
                    "
                    class="text-red-600 w-full block"
                >
                    {{
                        t(
                            "Table description must be less than 10000 characters"
                        )
                    }}.
                </small>
            </mat-form-field>

            <!-- Agents may insert and update rows (internal tables only) -->
            <div
                *ngIf="showAgentWriteToggle"
                class="w-full mt-3 add-tables-agent-write"
            >
                <mat-checkbox
                    formControlName="allowAgentWrite"
                    color="primary"
                    aria-describedby="add-tables-agent-write-desc"
                >
                    {{ t("Allow agents to insert and update table data") }}
                </mat-checkbox>
                <p
                    id="add-tables-agent-write-desc"
                    class="text-sm text-secondary ms-8 mt-1 mb-0"
                >
                    {{
                        t(
                            "Agents connected to this table can add rows and update existing rows through chat."
                        )
                    }}
                </p>
                <button
                    mat-stroked-button
                    type="button"
                    class="mt-3"
                    (click)="openTableHistory()"
                >
                    <mat-icon [svgIcon]="'heroicons_outline:clock'"></mat-icon>
                    <span class="ms-1">{{ t("Change history") }}</span>
                </button>
            </div>

                    </div>
                </section>

                <section
                    class="add-tables-section-card add-tables-section-card--structure"
                    *ngIf="data1?.type === 'View' || isExternalTableDialog()"
                >
                    <div class="add-tables-section-head add-tables-section-head--row">
                        <div class="add-tables-section-head-main">
                            <mat-icon class="add-tables-section-icon">account_tree</mat-icon>
                            <h2 class="add-tables-section-title">{{ t("Table Structure") }}</h2>
                        </div>
                        <div
                            *ngIf="!isExternalManagerStaffReadOnly"
                            class="flex gap-2 items-center flex-wrap justify-end shrink-0"
                        >
                            <button
                                type="button"
                                mat-stroked-button
                                (click)="toggleStructureView()"
                                class="text-xs"
                            >
                                <mat-icon class="text-sm">{{
                                    structureViewMode === "table"
                                        ? "description"
                                        : "table_chart"
                                }}</mat-icon>
                                {{
                                    structureViewMode === "table"
                                        ? t("Text View")
                                        : t("Table View")
                                }}
                            </button>
                        </div>
                    </div>
                    <div class="add-tables-section-body">

                <!-- Text Area View -->
                <mat-form-field
                    *ngIf="structureViewMode === 'text'"
                    class="w-full flex flex-col relative add-tables-field-scroll-through fuse-mat-dense"
                    [subscriptSizing]="'dynamic'"
                >
                    <button
                        type="button"
                        mat-icon-button
                        class="structure-text-copy-btn"
                        (click)="copyStructureTextViewValue()"
                        [matTooltip]="t('Copy')"
                        [attr.aria-label]="t('Copy')"
                    >
                        <mat-icon>content_copy</mat-icon>
                    </button>
                    <textarea
                        matInput
                        [value]="getStructureTextViewDisplayValue()"
                        maxlength="20000"
                        [rows]="6"
                        [spellcheck]="true"
                        cdkTextareaAutosize
                        cdkAutosizeMaxRows="6"
                        class="pe-10"
                        readonly
                    ></textarea>
                    <mat-hint align="end"
                        >{{ getStructureTextViewDisplayValue().length }}/20000</mat-hint
                    >
                    <small
                        *ngIf="
                            form.get('structure')?.touched &&
                            form.get('structure')?.hasError('maxlength')
                        "
                        class="text-red-600 w-full block"
                    >
                        {{
                            t(
                                "Table structure must be less than 20000 characters"
                            )
                        }}.
                    </small>
                </mat-form-field>



                <!-- Table View -->
                <div *ngIf="structureViewMode === 'table'" class="structure-table-container">
                    <!-- Table grain (API: root.grain or table_structure.grain, or tableInfo.grain) -->
                    <div *ngIf="parsedGrainRows.length || grainTemporalCoverageColumns.length || tableShapeColumns.length"
                        class="grain-panel mb-5 dark:border-gray-700 bg-white dark:bg-gray-800 flex flex-col gap-2">
                        <div *ngIf="parsedGrainRows.length" class="add-tables-scroll-x-only w-full border-gray-200 dark:border-gray-700">
                            <div class="add-tables-table-section-title add-tables-table-section-title--bordered grain-key-title-row">
                                {{ t("Table Grain") }}
                            </div>
                            <table class="add-tables-data-table min-w-full grain-table add-tables-wide-table">
                                <thead class="table-head-strong dark:bg-gray-700">
                                    <tr>
                                        <th
                                            class="px-4 py-3 text-left w-2/5 border-r dark:border-gray-600">
                                            {{ t("Attribute") }}
                                        </th>
                                        <th
                                            class="px-4 py-3 text-left">
                                            {{ t("Value") }}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    <tr *ngFor="let row of parsedGrainRows; let i = index"
                                        class="hover:bg-gray-50 dark:hover:bg-gray-700"
                                        [ngClass]="{ 'bg-gray-50/50': i % 2 === 1 }">
                                        <td class="px-4 py-3 align-top border-r dark:border-gray-600"
                                            [ngClass]="row.nested ? 'add-tables-data-table__muted-cell pl-8' : 'add-tables-data-table__primary-cell'">
                                            {{ t(row.label) }}
                                        </td>
                                        <td class="px-4 py-3 align-top break-words">
                                            <span>{{ row.value }}</span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div *ngIf="grainTemporalCoverageColumns.length || tableShapeColumns.length"
                            class="grid grid-cols-1 lg:grid-cols-2 gap-2">
                            <div *ngIf="tableShapeColumns.length" class="temporal-coverage-wrap">
                                <div class="add-tables-table-section-title temporal-coverage-title">
                                    {{ t("Table Shape") }}
                                </div>
                                <div class="temporal-coverage-chips">
                                    <div class="temporal-chip" *ngFor="let col of tableShapeColumns">
                                        <span class="temporal-chip-label">{{ t(col.label) }}</span>
                                        <span class="temporal-chip-value">{{ col.value }}</span>
                                    </div>
                                </div>
                            </div>

                            <div *ngIf="grainTemporalCoverageColumns.length" class="temporal-coverage-wrap">
                                <div class="add-tables-table-section-title temporal-coverage-title">
                                    {{ t("Temporal Coverage") }}
                                </div>
                                <div class="temporal-coverage-chips">
                                    <div class="temporal-chip" *ngFor="let col of grainTemporalCoverageColumns">
                                        <span class="temporal-chip-label">{{ t(col.label) }}</span>
                                        <span class="temporal-chip-value">{{ col.value }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                
                    <div class="column-structure-panel">
                        <div class="add-tables-table-section-title column-structure-title-row">
                            {{ t("Column Structure") }}
                        </div>
                        <app-structure-scope-note [scope]="statisticsScope"></app-structure-scope-note>
                        <div class="add-tables-scroll-x-only">
                            <table class="add-tables-data-table column-structure-table add-tables-wide-table divide-y divide-gray-200">
                            <thead class="table-head-strong dark:bg-gray-700 add-tables-table-head">
                                <tr>
                                    <th
                                        class="sticky-first-col px-4 py-3 text-left">
                                        {{ t("Column Name") }}
                                    </th>
                                    <th
                                        class="px-4 py-3 text-left border-r dark:border-gray-600">
                                        {{ t("Semantic Type") }}
                                    </th>
                                    <th *ngFor="
                                                            let columnKey of structureColumnKeys;
                                                            let last = last
                                                        "
                                        class="px-4 py-3 text-left"
                                        [ngClass]="[
                                            !last ? 'border-r dark:border-gray-600' : '',
                                            columnKey === 'description' ? 'structure-col-description' : ''
                                        ]">
                                        {{ t(getStructureColumnLabel(columnKey)) }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                <tr *ngFor="
                                                        let col of parsedStructure;
                                                        let i = index;
                                                        trackBy: trackByColumnStructure
                                                    " class="hover:bg-gray-50 dark:hover:bg-gray-700">
                                    <td
                                        class="sticky-first-col px-4 py-3 add-tables-data-table__primary-cell whitespace-nowrap">
                                        <div class="flex items-center gap-1 min-w-0">
                                            <button
                                                *ngIf="
                                                    !isForeignReadOnlyTable &&
                                                    !isExternalManagerStaffReadOnly
                                                "
                                                type="button"
                                                mat-icon-button
                                                class="!w-8 !h-8 shrink-0"
                                                [disabled]="isStructureColumnLocking(col)"
                                                (click)="
                                                    toggleStructureColumnLock(col);
                                                    $event.stopPropagation()
                                                "
                                                [matTooltip]="
                                                    isStructureColumnLocked(col)
                                                        ? t('Unlock this description. It is shared by every instance of this table.')
                                                        : t('Lock this description so it applies to every instance of this table.')
                                                "
                                                matTooltipTouchGestures="off"
                                            >
                                                <mat-icon class="icon-size-5 text-slate-600 dark:text-slate-300">
                                                    {{
                                                        isStructureColumnLocked(col)
                                                            ? 'lock'
                                                            : 'lock_open'
                                                    }}
                                                </mat-icon>
                                            </button>
                                            <span class="truncate">{{ col.name }}</span>
                                        </div>
                                    </td>
                                    <td class="px-4 py-3 add-tables-data-table__muted-cell border-r dark:border-gray-600 whitespace-nowrap"
                                        [class.opacity-60]="
                                                            !getSemanticTypeDisplay(col)
                                                        ">
                                        {{
                                        getSemanticTypeDisplay(col) || "-"
                                        }}
                                    </td>
                                    <td *ngFor="
                                                            let columnKey of structureColumnKeys;
                                                            let last = last
                                                        " class="px-4 py-3 add-tables-data-table__muted-cell"
                                        [ngClass]="[
                                            !last ? 'border-r dark:border-gray-600' : '',
                                            columnKey === 'description' ? 'structure-col-description' : ''
                                        ]" [class.whitespace-nowrap]="
                                                            columnKey !== 'description' &&
                                                            columnKey !== 'sample_values'
                                                        ">
                                        <ng-container *ngIf="columnKey === 'description'; else structureCellValue">
                                            <div class="flex items-start gap-1 min-w-0">
                                                <span #structureDescSpan class="structure-desc-text flex-1 min-w-0 break-words"
                                                    [attr.contenteditable]="
                                                                        !isForeignReadOnlyTable &&
                                                                        !isExternalManagerStaffReadOnly &&
                                                                        !isStructureColumnLocked(col) &&
                                                                        structureDescEditStates[i]
                                                                            ? 'true'
                                                                            : 'false'
                                                                    " (blur)="finishStructureDescEdit(i)">{{ col.description }}</span>
                                                <mat-icon *ngIf="
                                                                        !isForeignReadOnlyTable &&
                                                                        !isExternalManagerStaffReadOnly &&
                                                                        !structureDescEditStates[i] &&
                                                                        !isStructureColumnLocked(col)
                                                                    " (click)="startStructureDescEdit(i)"
                                                    class="edit-icon text-gradient text-xl shrink-0 cursor-pointer">edit</mat-icon>
                                                <mat-icon *ngIf="
                                                                        !isForeignReadOnlyTable &&
                                                                        !isExternalManagerStaffReadOnly &&
                                                                        structureDescEditStates[i]
                                                                    " (click)="finishStructureDescEdit(i)"
                                                    class="check-icon text-gradient shrink-0 cursor-pointer">check</mat-icon>
                                            </div>
                                        </ng-container>
                                        <ng-template #structureCellValue>
                                            <div class="max-w-md truncate" [matTooltip]="
                                                                    getStructureCellTooltip(
                                                                        col,
                                                                        columnKey
                                                                    )
                                                                " matTooltipTouchGestures="off">
                                                {{
                                                getStructureCellDisplay(
                                                col,
                                                columnKey
                                                )
                                                }}
                                            </div>
                                        </ng-template>
                                    </td>
                                </tr>
                                <tr *ngIf="parsedStructure.length === 0">
                                    <td [attr.colspan]="structureColumnKeys.length + 2" class="px-4 py-8 text-center text-gray-500">
                                        {{
                                        t(
                                        "No structure data available. Use Table View after AI extract, or Extract Table Structure Using
                                        AI."
                                        )
                                        }}
                                    </td>
                                </tr>
                            </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                    </div>
                </section>
        </form>

        <ng-container *ngIf="showColumnDataQualityFlagsSection">
            <section class="add-tables-section-card add-tables-section-card--core-logic mt-4">
                <div class="add-tables-section-head add-tables-section-head--row">
                    <div class="add-tables-section-head-main">
                        <mat-icon class="add-tables-section-icon">rule</mat-icon>
                        <h2 class="add-tables-section-title">{{ t("Data Quality Flags") }}</h2>
                    </div>
                </div>
                <div class="add-tables-section-body">
                    <div class="add-tables-table-frame data-quality-table">
                        <div class="add-tables-scroll-x-only">
                        <table class="add-tables-data-table min-w-full data-quality-inner-table add-tables-wide-table">
                            <thead class="table-head-strong dark:bg-gray-700 add-tables-table-head">
                                <tr>
                                    <th
                                        class="data-quality-col-name px-4 py-3 text-left border-r dark:border-gray-600"
                                    >
                                        {{ t("Column Name") }}
                                    </th>
                                    <th
                                        class="data-quality-col-flags px-4 py-3 text-left"
                                    >
                                        {{ t("Flags") }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr *ngFor="let entry of columnDataQualityFlagEntries">
                                    <td
                                        class="data-quality-col-name px-4 py-2 border-r dark:border-gray-600 add-tables-data-table__primary-cell"
                                    >
                                        {{ entry.columnName }}
                                    </td>
                                    <td class="data-quality-col-flags px-4 py-2">
                                        {{ entry.flags.join(" | ") }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        </div>
                    </div>
                </div>
            </section>
        </ng-container>

        <!-- Core Logic (KPIs): only when viewing an existing table; hidden during new-file import -->
        <ng-container *ngIf="showCoreLogicSection">
            <section class="add-tables-section-card add-tables-section-card--core-logic mt-4">
                <div class="add-tables-section-head add-tables-section-head--row">
                    <div class="add-tables-section-head-main">
                        <mat-icon class="add-tables-section-icon">functions</mat-icon>
                        <h2 class="add-tables-section-title">{{ t("Core Logic") }}</h2>
                    </div>
                <div
                    class="flex flex-wrap items-center gap-2 shrink-0 justify-end"
                    *ngIf="
                        metricDefinitions &&
                        !isForeignReadOnlyTable &&
                        !isExternalManagerStaffReadOnly
                    "
                >
                    <button
                        type="button"
                        class="bg-primary min-w-fit text-md px-3 py-2 
                rounded-full shadow-md hover:shadow-2xl focus:outline-none d-flex items-center gap-2"
                        (click)="openAddKpiDialog()"
                        [disabled]="isKpiAddDisabled || isRegeneratingKpis"
                    >
                        <mat-icon class="button-icon">add</mat-icon>
                        {{ t("Add KPI") }}
                    </button>
                    <button
                        type="button"
                        class="bg-primary min-w-fit text-md px-3 py-2 
                rounded-full shadow-md hover:shadow-2xl focus:outline-none d-flex items-center gap-2"
                        (click)="regenerateKpis()"
                        [disabled]="isRegenerateKpisDisabled"
                    >
                        <mat-icon class="button-icon">sync</mat-icon>
                        {{ t("Regenerate KPIs") }}
                    </button>
                    <button
                        type="button"
                        mat-icon-button
                        class="bg-primary rounded-full shadow-md hover:shadow-2xl focus:outline-none d-flex items-center justify-center"
                        [matMenuTriggerFor]="coreLogicActionsMenu"
                        [disabled]="
                            isRegeneratingKpis ||
                            isDeletingKpi ||
                            isSavingImportedCoreLogic
                        "
                        [matTooltip]="t('Core Logic Actions')"
                        [attr.aria-label]="t('Core Logic Actions')"
                    >
                        <mat-icon class="button-icon">more_vert</mat-icon>
                    </button>
                    <mat-menu #coreLogicActionsMenu="matMenu" class="sai-menu">
                        <button
                            *ngIf="!coreLogicImportedPendingSave"
                            mat-menu-item
                            type="button"
                            (click)="openCoreLogicImportGuidelines()"
                            [disabled]="
                                isRegeneratingKpis ||
                                isDeletingKpi ||
                                isSavingImportedCoreLogic
                            "
                        >
                            <mat-icon>upload_file</mat-icon>
                            <span>{{ t("Import CSV") }}</span>
                        </button>
                        <button
                            *ngIf="coreLogicImportedPendingSave"
                            mat-menu-item
                            type="button"
                            (click)="saveImportedCoreLogic()"
                            [disabled]="isSavingImportedCoreLogic"
                        >
                            <mat-icon>save</mat-icon>
                            <span>{{ t("Save Imported") }}</span>
                        </button>
                        <button
                            *ngIf="coreLogicImportedPendingSave"
                            mat-menu-item
                            type="button"
                            (click)="removeImportedCoreLogic()"
                            [disabled]="isSavingImportedCoreLogic"
                        >
                            <mat-icon [svgIcon]="'heroicons_outline:trash'"></mat-icon>
                            <span>{{ t("Remove Imported") }}</span>
                        </button>
                        <button
                            mat-menu-item
                            type="button"
                            (click)="exportCoreLogicAsCsv()"
                            [disabled]="
                                isRegeneratingKpis ||
                                isDeletingKpi ||
                                isSavingImportedCoreLogic ||
                                !metricDefinitions?.length
                            "
                        >
                            <mat-icon>download</mat-icon>
                            <span>{{ t("Export CSV") }}</span>
                        </button>
                    </mat-menu>
                </div>
                </div>
                <div class="add-tables-section-body">
            <div class="add-tables-table-frame core-logic-table">
                <div
                    *ngIf="
                        isRegeneratingKpis ||
                        isDeletingKpi ||
                        isSavingImportedCoreLogic
                    "
                    class="table-preview-loading-overlay core-logic-loading-overlay"
                >
                    <mat-progress-spinner
                        class="custom-spinner"
                        mode="indeterminate"
                    ></mat-progress-spinner>
                </div>
                <div class="add-tables-scroll-x-only">
                <table class="add-tables-data-table min-w-full core-logic-inner-table add-tables-wide-table">
                    <thead class="table-head-strong dark:bg-gray-700 add-tables-table-head">
                        <tr>
                            <th
                                class="core-logic-col-level px-4 py-3 text-left border-r dark:border-gray-600"
                            >
                                {{ t("Level") }}
                            </th>
                            <th
                                class="core-logic-col-name px-4 py-3 text-left border-r dark:border-gray-600"
                            >
                                {{ t("KPI Name") }}
                            </th>
                            <th
                                class="core-logic-col-calc px-4 py-3 text-left border-r dark:border-gray-600"
                            >
                                {{ t("Calculation") }}
                            </th>
                            <th
                                class="core-logic-col-desc px-4 py-3 text-left border-r dark:border-gray-600"
                            >
                                {{ t("Description") }}
                            </th>
                            <th
                                *ngIf="
                                    !isForeignReadOnlyTable &&
                                    !isExternalManagerStaffReadOnly
                                "
                                class="core-logic-col-actions px-4 py-3 text-center"
                            >
                                {{ t("Actions") }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr *ngFor="let metric of metricDefinitions; let i = index">
                            <td class="core-logic-col-level px-2 py-2 border-r">
                                <div class="flex items-center gap-1 min-w-0">
                                    <button
                                        *ngIf="
                                            !isForeignReadOnlyTable &&
                                            !isExternalManagerStaffReadOnly
                                        "
                                        type="button"
                                        mat-icon-button
                                        class="!w-8 !h-8 shrink-0"
                                        [disabled]="
                                            metric.id == null ||
                                            isRegeneratingKpis ||
                                            isDeletingKpi ||
                                            isKpiLocking(metric)
                                        "
                                        (click)="
                                            toggleKpiLock(metric, i);
                                            $event.stopPropagation()
                                        "
                                        [matTooltip]="
                                            metric.isLocked
                                                ? t('Unlock KPI')
                                                : t('Lock KPI')
                                        "
                                        matTooltipTouchGestures="off"
                                    >
                                        <mat-icon class="icon-size-5 text-slate-600 dark:text-slate-300">
                                            {{
                                                metric.isLocked
                                                    ? 'lock'
                                                    : 'lock_open'
                                            }}
                                        </mat-icon>
                                    </button>
                                    <span
                                        [matTooltip]="metric.level"
                                        matTooltipTouchGestures="off"
                                    >
                                        {{ metric.level }}
                                    </span>
                                </div>
                            </td>
                            <td
                                class="core-logic-col-name px-4 py-2 border-r add-tables-data-table__primary-cell"
                                [matTooltip]="metric.kpi_name"
                                matTooltipTouchGestures="off"
                            >
                                {{ metric.kpi_name }}
                            </td>
                            <td class="core-logic-col-calc px-4 py-2 border-r add-tables-data-table__code-cell">
                                <div class="flex items-start gap-1 min-w-0">
                                    <span
                                        class="core-logic-formula-text flex-1"
                                        [matTooltip]="metric.calculation"
                                        matTooltipTouchGestures="off"
                                    >{{
                                        metric.calculation
                                    }}</span>
                                    <mat-icon
                                        *ngIf="
                                            !isForeignReadOnlyTable &&
                                            !isExternalManagerStaffReadOnly &&
                                            !coreLogicImportedPendingSave
                                        "
                                        class="edit-icon text-gradient text-xl shrink-0 cursor-pointer"
                                        [class.opacity-40]="metric.isLocked"
                                        [class.pointer-events-none]="metric.isLocked"
                                        (click)="
                                            !metric.isLocked &&
                                                openFormulaPromptDialog(i)
                                        "
                                        >edit</mat-icon
                                    >
                                </div>
                            </td>
                            <td
                                class="core-logic-col-desc px-4 py-2 border-r dark:border-gray-600"
                                [matTooltip]="metric.description"
                                matTooltipTouchGestures="off"
                            >
                                {{ metric.description }}
                            </td>
                            <td
                                *ngIf="
                                    !isForeignReadOnlyTable &&
                                    !isExternalManagerStaffReadOnly
                                "
                                class="core-logic-col-actions px-2 py-2 text-center align-middle"
                            >
                                <button
                                    type="button"
                                    mat-icon-button
                                    class="sai-card-action-btn !w-9 !h-9"
                                    [disabled]="
                                        isDeletingKpi ||
                                        isRegeneratingKpis ||
                                        metric.id == null ||
                                        metric.isLocked ||
                                        isKpiLocking(metric)
                                    "
                                    (click)="confirmDeleteKpi(i)"
                                    [matTooltip]="
                                        metric.isLocked
                                            ? t('Unlock this KPI before deleting')
                                            : t('Delete KPI')
                                    "
                                >
                                    <mat-icon [svgIcon]="'heroicons_outline:trash'"></mat-icon>
                                </button>
                            </td>
                        </tr>
                        <tr *ngIf="metricDefinitions.length === 0">
                            <td
                                [attr.colspan]="
                                    isForeignReadOnlyTable ||
                                    isExternalManagerStaffReadOnly
                                        ? 4
                                        : 5
                                "
                                class="px-4 py-8 text-center text-gray-500"
                            >
                                {{ t("No core logic data available") }}
                            </td>
                        </tr>
                    </tbody>
                </table>
                </div>
            </div>
                </div>
            </section>
        </ng-container>

            </div>

            <button
                *ngIf="
                    canToggleTablePreviewCollapse && isTablePreviewCollapsed
                "
                type="button"
                class="add-tables-preview-expand-tab"
                (click)="toggleTablePreviewCollapsed()"
                [matTooltip]="t('Show table preview')"
                [attr.aria-label]="t('Show table preview')"
            >
                <mat-icon [svgIcon]="tablePreviewCollapseChevronIcon"></mat-icon>
                <span class="add-tables-preview-expand-tab__label">{{
                    t("Table Preview")
                }}</span>
            </button>

            <aside class="content-right add-tables-preview-aside" *ngIf="showTablePreviewPane">
                <div class="table-preview-section add-tables-preview-section">
                    <div class="flex justify-between items-center flex-shrink-0 mb-2 gap-2">
                        <label class="text-sm section-title">{{
                            t("Table Preview")
                        }}</label>
                        <button
                            *ngIf="canToggleTablePreviewCollapse"
                            type="button"
                            mat-icon-button
                            class="add-tables-preview-collapse-btn sai-card-action-btn"
                            (click)="toggleTablePreviewCollapsed()"
                            [matTooltip]="t(tablePreviewCollapseTooltipKey)"
                            [attr.aria-label]="t(tablePreviewCollapseTooltipKey)"
                            [attr.aria-expanded]="!isTablePreviewCollapsed"
                        >
                            <mat-icon
                                [svgIcon]="tablePreviewCollapseChevronIcon"
                            ></mat-icon>
                        </button>
                    </div>

        <!-- Data table -->
        <div
            class="table-container table-preview add-tables-preview-pane"
            [ngClass]="{
                'view-mode': data1?.type === 'View' && !isAppending,
                'view-external-mode': isExternalTableDialog(),
                'add-mode': data1?.type === 'Add' || isAppending,
                'table-preview-loading': isTablePreviewLoading
            }">
            <!-- Initial load: no headers yet — fill the pane with the shared spinner -->
            <div
                *ngIf="isTablePreviewLoading && !headers.length"
                class="add-tables-preview-empty"
            >
                <app-loading-spinner
                    [diameter]="48"
                    [loadingText]="t('Loading table data')"
                    [showLoadingText]="true"
                    containerClass="!min-h-0 !py-8 sm:!py-10 md:!py-12"
                ></app-loading-spinner>
            </div>

            <!-- Empty: fetch finished with no columns/rows -->
            <div
                *ngIf="!isTablePreviewLoading && !headers.length"
                class="add-tables-preview-empty"
            >
                <app-no-data-found
                    class="w-full"
                    [compact]="true"
                    icon="heroicons_outline:table-cells"
                    titleKey="No data available"
                    messageKey="No table preview data is available."
                ></app-no-data-found>
            </div>

            <!-- Table (keep visible under overlay during page reloads) -->
            <ng-container *ngIf="headers.length">
            <div
                *ngIf="isTablePreviewLoading"
                class="table-preview-loading-overlay"
            >
                <app-loading-spinner
                    [diameter]="48"
                    [showLoadingText]="false"
                    containerClass="!min-h-0 !py-0"
                ></app-loading-spinner>
            </div>

            <div class="add-tables-preview-scroll-x">
            <table class="add-tables-data-table add-tables-wide-table">
                <thead class="table-head-strong dark:bg-gray-700 add-tables-table-head">
                    <tr>
                        <th
                            class="px-4 py-3 text-left border-r dark:border-gray-600"
                            [class.add-tables-col--invalid]="isColumnInvalid(i)"
                            [matTooltip]="columnProblemTooltip(i)"
                            *ngFor="
                                let h of headers;
                                let i = index;
                                trackBy: trackByHeader
                            "
                        >
                            <div [id]="'header-' + i" class="header-cell">
                                <span
                                    #headerSpan
                                    class="header-text"
                                    [attr.contenteditable]="
                                        (data1?.type === 'Add' ||
                                            isAppending) &&
                                        editStates[i]
                                            ? 'true'
                                            : 'false'
                                    "
                                    (blur)="toggleEdit(i)"
                                    >{{ h }}</span
                                >

                                <ng-container
                                    *ngIf="data1?.type === 'Add' || isAppending"
                                >
                                    <mat-icon
                                        *ngIf="!editStates[i]"
                                        (click)="toggleEdit(i)"
                                        class="edit-icon text-gradient text-xl"
                                        >edit</mat-icon
                                    >
                                    <mat-icon
                                        *ngIf="editStates[i]"
                                        (click)="toggleEdit(i)"
                                        class="check-icon text-gradient"
                                        >check</mat-icon
                                    >
                                </ng-container>
                            </div>
                            <span
                                *ngIf="isColumnInvalid(i)"
                                class="sai-badge sai-badge--rect sai-badge--danger add-tables-col-issue"
                            >{{ columnProblemNote(i) }}</span>
                        </th>
                    </tr>

                    <!-- Column type selector (Add or Append edit mode) -->
                    <tr *ngIf="data1?.type === 'Add' || isAppending">
                        <th
                            *ngFor="
                                let ctrl of columnTypesFA.controls;
                                let k = index
                            "
                        >
                            <mat-form-field class="w-full fuse-mat-dense">
                                <mat-select
                                    [value]="ctrl.value"
                                    (selectionChange)="
                                        onTypeChange(k, $event.value)
                                    "
                                >
                                    <mat-option value="DECIMAL">{{
                                        t("DECIMAL")
                                    }}</mat-option>
                                    <mat-option value="INTEGER">{{
                                        t("INTEGER")
                                    }}</mat-option>
                                    <mat-option value="TEXT">{{
                                        t("TEXT")
                                    }}</mat-option>
                                    <mat-option value="DATE">{{
                                        t("DATE")
                                    }}</mat-option>
                                    <mat-option value="BIGINT">{{
                                        t("BIGINT")
                                    }}</mat-option>
                                </mat-select>
                            </mat-form-field>
                        </th>
                    </tr>

                    <!-- Units selector -->
                    <tr *ngIf="data1?.type === 'Add' || isAppending">
                        <th
                            class="px-4 py-2"
                            [class.border-r]="i < headers.length - 1"
                            *ngFor="
                                let unitCtrl of unitsFA.controls;
                                let i = index
                            "
                        >
                            <ng-container [ngSwitch]="columnTypesFA.value[i]">
                                <ng-container *ngSwitchCase="'DATE'">
                                    <mat-form-field class="w-full fuse-mat-dense">
                                        <mat-select [formControl]="unitCtrl" [placeholder]="t('Choose format')">
                                            <mat-option value="DD-MM-YYYY"
                                                >DD-MM-YYYY</mat-option
                                            >
                                            <mat-option value="MM-DD-YYYY"
                                                >MM-DD-YYYY</mat-option
                                            >
                                            <mat-option value="YYYY-MM-DD"
                                                >YYYY-MM-DD</mat-option
                                            >
                                            <mat-option value="YYYY-DD-MM"
                                                >YYYY-DD-MM</mat-option
                                            >
                                        </mat-select>
                                    </mat-form-field>
                                </ng-container>

                                <ng-container *ngSwitchDefault>
                                    <mat-form-field class="w-full fuse-mat-dense">
                                        <mat-select [formControl]="unitCtrl">
                                            <mat-option
                                                *ngFor="
                                                    let opt of unitOptions;
                                                    trackBy: trackByHeader
                                                "
                                                [value]="opt.value"
                                            >
                                                {{ t(opt.name) }}
                                            </mat-option>
                                        </mat-select>
                                    </mat-form-field>
                                </ng-container>
                            </ng-container>
                        </th>
                    </tr>
                </thead>

                <tbody>
                    <tr
                        *ngFor="
                            let row of rows;
                            let j = index;
                            trackBy: trackByRow
                        "
                    >
                        <td
                            *ngFor="let cell of row; let ci = index; trackBy: trackByRow"
                            [appCopyOnDblclick]="cell"
                            class="copy-cell bg-white hover:bg-gray-100"
                        >
                            {{
                                formatTablePreviewCell(headers[ci], cell)
                            }}
                        </td>
                    </tr>
                    <tr *ngIf="!rows.length">
                        <td
                            [attr.colspan]="headers.length || 1"
                            class="px-4 py-8 text-center text-gray-500"
                        >
                            {{ t("No data available") }}
                        </td>
                    </tr>
                </tbody>
            </table>
            </div>
            </ng-container>

            <!-- Paginator: the preview's footer, directly under its scroll area -->
            <mat-paginator
                *ngIf="headers.length"
                [length]="length"
                [pageSize]="pageSize"
                [pageSizeOptions]="[50, 100, 200, 500]"
                (page)="onPageChange($event)"
            >
            </mat-paginator>
        </div>
                </div>
            </aside>
        </div>
        </div>

        <!-- Footer actions (PATTERNS.md §7: primary actions live in the footer, not the band) -->
        <div
            *ngIf="hasFooterActions"
            class="dialog-footer add-tables-footer flex flex-wrap items-center justify-end gap-2"
        >
            <button
                *ngIf="isAppending"
                type="button"
                mat-stroked-button
                (click)="appendNow()"
                [disabled]="loading || loadingOnSubmit"
            >
                <mat-icon>add_circle</mat-icon>
                {{ t("Append Now") }}
            </button>

            <button
                *ngIf="
                    data1?.type === 'View' &&
                    !isAppending &&
                    data1?.isMyTable !== false
                "
                type="button"
                mat-stroked-button
                (click)="handleAppendClick()"
            >
                <mat-icon>add_box</mat-icon>
                {{ t("Append New Data") }}
            </button>

            <button
                appDisableWhileLoading
                *ngIf="
                    data1?.isMyTable !== false &&
                    (data1?.type === 'View' || isExternalTableDialog())
                "
                type="button"
                mat-stroked-button
                (click)="extractTableStructure()"
                [disabled]="extractTableLoader"
            >
                @if (extractTableLoader) {
                    <mat-progress-spinner
                        mode="indeterminate"
                        class="add-tables-footer-spinner"
                        diameter="20"
                    ></mat-progress-spinner>
                } @else {
                    <mat-icon>auto_awesome</mat-icon>
                }
                {{ t("Extract Table Structure") }}
            </button>

            <button
                *ngIf="
                    ((data1?.isMyTable && data1?.type === 'Add') ||
                        (data1?.isMyTable && data1?.type === 'View') ||
                        isAppending) &&
                    isDevEnv
                "
                type="button"
                mat-stroked-button
                [disabled]="isSaveButtonDisabled"
                (click)="downloadGzip()"
            >
                <mat-icon>download</mat-icon>
                {{ t("Download gzip") }}
            </button>

            <button
                *ngIf="isSaveButtonVisible"
                type="button"
                class="inline-flex items-center justify-center gap-2"
                mat-flat-button
                [disabled]="isSaveButtonDisabled"
                [class.bg-primary]="!isSaveButtonDisabled"
                [class.bg-invalid]="isSaveButtonDisabled"
                (click)="submit()"
            >
                <mat-icon class="icon-size-4 shrink-0 button-icon">save</mat-icon>
                <span
                    *ngIf="!loading && !loadingOnSubmit && !savingExternalMeta"
                    >{{ t("Save") }}</span
                >
                <span
                    *ngIf="loading || loadingOnSubmit || savingExternalMeta"
                    class="loading-button"
                >
                    <span class="loading-dots"
                        ><span></span><span></span><span></span
                    ></span>
                    {{ t("Loading") }}
                </span>
            </button>
        </div>
    </div>

</ng-container>
